import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Radar as RadarBase, RadarChart as RadarChartBase, PolarGrid as PolarGridBase,
  PolarAngleAxis as PolarAngleAxisBase, PolarRadiusAxis as PolarRadiusAxisBase,
  ResponsiveContainer, Legend as LegendBase, Tooltip as TooltipBase,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import ProfessionalHeader from '../components/ProfessionalHeader';
import {
  lookupCatalog, getSimilar, ExploreTarget, HeadLookupItem, ShaftLookupItem,
  HeadSpec, ShaftSpec, SimilarResponse,
} from '../services/exploreService';

// recharts 3.8's JSX types don't resolve cleanly against this project's React
// types (see components/charts/RadarChart.tsx for the same workaround) --
// cast once here rather than sprinkling `as any` through the markup below.
const Radar = RadarBase as any;
const RadarChart = RadarChartBase as any;
const PolarGrid = PolarGridBase as any;
const PolarAngleAxis = PolarAngleAxisBase as any;
const PolarRadiusAxis = PolarRadiusAxisBase as any;
const Legend = LegendBase as any;
const Tooltip = TooltipBase as any;

// X8 -- EXPERT_PORTAL_UX_PLAN.md §8: "look up a real club, see plausible
// neighbours by similarity, on real catalog data." Driver heads and driver
// shafts only -- the case §2's catalog audit actually verified as populated.
// Scope decision: the "entry point to bay mode" link here just navigates to
// /expert/bay?profile_id=... (when that param is already present) -- it does
// not pre-fill a build there. Wiring an ad-hoc head/shaft pick into bay
// mode's capture flow is follow-up scope, not part of this session's row.

const NEIGHBOR_COLORS = ['#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed'];

function isNumeric(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function label(target: ExploreTarget, item: any): string {
  return target === 'head'
    ? `${item.manufacturer} ${item.model}${item.loft != null ? ` (${item.loft}°)` : ''}`
    : `${item.shaft_brand} ${item.shaft_model}${item.standard_flex ? ` (${item.standard_flex})` : ''}`;
}

const ExplorePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const profileIdParam = searchParams.get('profile_id');

  const [target, setTarget] = useState<ExploreTarget>('head');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<(HeadLookupItem | ShaftLookupItem)[]>([]);
  const [searching, setSearching] = useState(false);

  const [anchorId, setAnchorId] = useState<number | null>(null);
  const [similar, setSimilar] = useState<SimilarResponse<HeadSpec | ShaftSpec> | null>(null);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounced lookup as the fitter types.
  useEffect(() => {
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const data = await lookupCatalog(target, query, 15);
        if (!cancelled) setResults(data);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [target, query]);

  const selectAnchor = async (id: number) => {
    setAnchorId(id);
    setSimilar(null);
    setError(null);
    setLoadingSimilar(true);
    try {
      const data = await getSimilar(target, id, 8);
      setSimilar(data);
    } catch (err) {
      setError('Could not load neighbours for this item.');
    } finally {
      setLoadingSimilar(false);
    }
  };

  const switchTarget = (t: ExploreTarget) => {
    setTarget(t);
    setQuery('');
    setResults([]);
    setAnchorId(null);
    setSimilar(null);
  };

  // Numeric features only -- cg_height/cg_distance and launch/spin are bands
  // (shown as text below), not plotted, so the radar doesn't need this
  // module's ordinal-band mapping duplicated in the frontend.
  const radarData = useMemo(() => {
    if (!similar) return [];
    const shown = similar.neighbors.slice(0, 4);
    const numericFeatures = similar.features.filter((f) => isNumeric((similar.anchor as any)[f]));
    return numericFeatures.map((f) => {
      const values = [
        (similar.anchor as any)[f],
        ...shown.map((n) => (n as any)[f]).filter(isNumeric),
      ];
      const min = Math.min(...values);
      const max = Math.max(...values);
      const scale = (v: number) => (max === min ? 50 : ((v - min) / (max - min)) * 100);
      const row: Record<string, any> = { axis: f.replace(/_/g, ' ') };
      row.anchor = scale((similar.anchor as any)[f]);
      shown.forEach((n, i) => {
        const v = (n as any)[f];
        row[`n${i}`] = isNumeric(v) ? scale(v) : null;
      });
      return row;
    });
  }, [similar]);

  const anchorLabel = similar ? label(target, similar.anchor) : '';
  const shownNeighbors = similar ? similar.neighbors.slice(0, 4) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <ProfessionalHeader user={user} onLogout={logout} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Explore the catalog</h1>
            <p className="text-gray-600 mt-1">
              Look up a real driver head or shaft and see its nearest neighbours by spec,
              not by price. Driver only, for now.
            </p>
          </div>
          {profileIdParam && (
            <button
              onClick={() => navigate(`/expert/bay?profile_id=${profileIdParam}`)}
              className="px-5 py-2.5 rounded-lg font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              &larr; Back to bay mode (profile #{profileIdParam})
            </button>
          )}
        </div>

        {/* Target toggle + search */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => switchTarget('head')}
              className={`px-4 py-2 rounded-lg font-semibold ${target === 'head' ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              Driver heads
            </button>
            <button
              onClick={() => switchTarget('shaft')}
              className={`px-4 py-2 rounded-lg font-semibold ${target === 'shaft' ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              Driver shafts
            </button>
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={target === 'head' ? 'Search manufacturer or model (e.g. "TaylorMade Qi10")' : 'Search brand or model (e.g. "TENSEI")'}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {searching && <div className="text-sm text-gray-400 mt-2">Searching&hellip;</div>}
          {!searching && results.length > 0 && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
              {results.map((r: any) => (
                <button
                  key={r.id}
                  onClick={() => selectAnchor(r.id)}
                  className={`text-left px-3 py-2 rounded-lg border text-sm ${anchorId === r.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  {label(target, r)}
                </button>
              ))}
            </div>
          )}
          {!searching && query && results.length === 0 && (
            <div className="text-sm text-gray-400 mt-2">No driver {target}s match &ldquo;{query}&rdquo;.</div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">{error}</div>
        )}

        {loadingSimilar && (
          <div className="text-gray-500 mb-6">Finding neighbours&hellip;</div>
        )}

        {similar && (
          <>
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <div className="text-sm text-gray-500 uppercase tracking-wide mb-1">Anchor</div>
              <div className="text-xl font-semibold text-gray-900">{anchorLabel}</div>
              <div className="text-sm text-gray-500 mt-1">
                Compared against {similar.population_size} driver {target}s on {similar.features.join(', ')}.
              </div>
            </div>

            {radarData.length > 0 && (
              <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                <div className="text-sm text-gray-500 uppercase tracking-wide mb-3">
                  Spec shape — anchor vs. top {shownNeighbors.length} neighbours (min-max scaled per axis, this comparison set only)
                </div>
                <ResponsiveContainer width="100%" height={340}>
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="axis" tick={{ fontSize: 12 }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                    <Tooltip />
                    <Legend />
                    <Radar name={anchorLabel} dataKey="anchor" stroke="#111827" fill="#111827" fillOpacity={0.25} strokeWidth={2} />
                    {shownNeighbors.map((n, i) => (
                      <Radar
                        key={(n as any).id}
                        name={label(target, n)}
                        dataKey={`n${i}`}
                        stroke={NEIGHBOR_COLORS[i]}
                        fill={NEIGHBOR_COLORS[i]}
                        fillOpacity={0.08}
                      />
                    ))}
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="text-sm text-gray-500 uppercase tracking-wide mb-3">Nearest neighbours</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-4">#</th>
                      <th className="py-2 pr-4">Item</th>
                      <th className="py-2 pr-4">Similarity</th>
                      {target === 'head' ? (
                        <>
                          <th className="py-2 pr-4">MOI</th>
                          <th className="py-2 pr-4">CG</th>
                        </>
                      ) : (
                        <>
                          <th className="py-2 pr-4">Weight / Torque</th>
                          <th className="py-2 pr-4">Launch / Spin</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {similar.neighbors.map((n: any, i) => (
                      <tr key={n.id} className="border-b last:border-0">
                        <td className="py-2 pr-4 text-gray-400">{i + 1}</td>
                        <td className="py-2 pr-4 font-medium text-gray-900">{label(target, n)}</td>
                        <td className="py-2 pr-4">{Math.round((n.similarity ?? 0) * 100)}%</td>
                        {target === 'head' ? (
                          <>
                            <td className="py-2 pr-4">
                              {n.moi_combined ?? '—'}
                              {n.moi_source === 'Estimate' && (
                                <span className="ml-1 text-xs text-amber-600" title="MOI is estimated, not measured">est.</span>
                              )}
                            </td>
                            <td className="py-2 pr-4">{n.cg_distance ?? '—'} / {n.cg_height ?? '—'}</td>
                          </>
                        ) : (
                          <>
                            <td className="py-2 pr-4">{n.weight ?? '—'}g / {n.torque ?? '—'}&deg;</td>
                            <td className="py-2 pr-4">{n.launch ?? '—'} / {n.spin ?? '—'}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExplorePage;
